import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  text: string;
};

interface Props {
  onClose: () => void;
}

const BlacklistForm: FC<Props> = ({ onClose }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      text: '',
    },
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post('/v1/editorial/queue/blacklisted-keywords', data);

        await queryClient.invalidateQueries(['dq_blacklisted_keywords']);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal zIndex={50}>
      <SidePanel heading={`New blacklisted keyword`} onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                label="Keyword"
                name="text"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Add
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default BlacklistForm;
